import { useGSAP, gsap } from "@/shared/lib/gsap";
import { RefObject } from "react";

interface UseIntroAnimationProps {
  shouldShowIntro: boolean;
  sectionRef: RefObject<HTMLDivElement | null>;
  logoRef: RefObject<HTMLDivElement | null>;
  onComplete: () => void;
}

export function useIntroAnimation({ shouldShowIntro, sectionRef, logoRef, onComplete }: UseIntroAnimationProps) {
  useGSAP(
    () => {
      if (!shouldShowIntro) return;

      const section = sectionRef.current;
      const logo = logoRef.current;

      if (!section || !logo) return;

      // 헤더 로고 위치 계산
      const headerLogo = document.querySelector("[data-header-logo]") as HTMLElement;

      // 브레이크포인트 판단 (useBreakpoint와 동일한 기준)
      const isMobile = window.innerWidth <= 799;
      const isTablet = window.innerWidth >= 800 && window.innerWidth <= 1280;
      const isPc = window.innerWidth > 1280;

      let targetX = -window.innerWidth / 2 + 120;
      let targetY = -window.innerHeight / 2 + 40;
      let targetScale = 0.6;

      if (headerLogo) {
        const headerLogoRect = headerLogo.getBoundingClientRect();
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;

        targetX = headerLogoRect.left + headerLogoRect.width / 2 - centerX;
        targetY = headerLogoRect.top + headerLogoRect.height / 2 - centerY;

        // 브레이크포인트에 따른 인트로 로고 크기
        const introLogoWidth = isPc ? 272.42 : 170; // 모바일/태블릿은 170px
        targetScale = headerLogoRect.width / introLogoWidth;
      }

      // 타임라인 생성
      const animation = gsap.timeline({
        onComplete: () => {
          const container = section.parentElement;
          if (container) {
            container.style.display = "none";
          }

          // 히어로 콘텐츠 표시
          const heroContent = document.querySelector("[data-hero-content]");

          if (heroContent) {
            gsap.to(heroContent, { opacity: 1, duration: 0.5 });
          }

          onComplete();
        },
      });

      // 1단계: 로고 서서히 등장
      animation
        .fromTo(
          logo,
          {
            opacity: 0,
            scale: 0.5,
            y: 300,
          },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            duration: 1.5,
            ease: "power2.out",
          },
        )
        // 2단계: 잠깐 대기
        .to({}, { duration: 1 })
        // 3단계: 헤더 로고 위치로 이동과 동시에 헤더 표시
        .to(logo, {
          x: targetX,
          y: targetY,
          scale: targetScale,
          duration: 1,
          ease: "power2.inOut",
        })
        .add(() => {
          // 로고 이동 중간에 헤더 표시
          const header = document.querySelector("header");
          if (header) {
            gsap.to(header, { opacity: 1, duration: 0.3 });
          }
        }, "-=0.5")
        // 4단계: 로고 페이드 아웃
        .to(
          logo,
          {
            opacity: 0,
            duration: 0.8,
            ease: "power2.in",
          },
          "-=0.5",
        )
        // 5단계: 섹션 전체 서서히 사라짐
        .to(
          section,
          {
            opacity: 0,
            duration: 0.7,
            ease: "power2.inOut",
          },
          "-=0.5",
        );
    },
    { dependencies: [shouldShowIntro] },
  );
}
